import React from 'react';
import { Button } from 'primereact/button';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NotificationBell from '../../molecules/NotificationBell/NotificationBell';
import { FALLBACK_METASPACE_SYSTEM_NAME } from '../../../constants/metaspaces';
import './global-metaspaces.scss';

const GlobalMetaspaceContainer = styled.div`
  padding: 20px;
  padding-right: 32px;
  padding-top: 5px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface GlobalMetaspacesProps {
  sphereId: string;
  metaspaceId?: string;
}

const GlobalMetaspaces: React.FC<GlobalMetaspacesProps> = ({ sphereId, metaspaceId }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleMetaspaceClick = (targetSystemName: string) => {
    navigate(`/sphere/${sphereId}/metaspace/${targetSystemName}`);
  }

  const handleViewAllNotifications = () => {
    navigate('/notifications');
  };

  // Marketplace and Studio use hash routes, not metaspace routes — check URL directly.
  const isMarketplaceActive = globalThis.location.hash?.includes('/marketplace');
  const isStudioActive = globalThis.location.hash?.includes('/studio');
  // The remaining three check the metaspaceId prop, but must yield to marketplace/studio
  // when the user navigates there (metaspaceId prop can lag behind the hash route).
  const isArmillaryActive =
    !isMarketplaceActive && !isStudioActive && metaspaceId === FALLBACK_METASPACE_SYSTEM_NAME;
  const isAtlasActive =
    !isMarketplaceActive && !isStudioActive && metaspaceId === 'center_global_metaspace';
  const isTaskListActive =
    !isMarketplaceActive && !isStudioActive && metaspaceId === 'center_task_list';

  return (
    <GlobalMetaspaceContainer className="globalmetaspace-container">
      <Button
        icon={isArmillaryActive ? 'Armillary-icon-filled' : 'Armillary-icon'}
        className="global-armillary-button"
        onClick={() => handleMetaspaceClick(FALLBACK_METASPACE_SYSTEM_NAME)}
      />
      <Button
        icon={isAtlasActive ? 'Atlas-icon-filled' : 'Atlas-icon'}
        className="global-atlas-button"
        onClick={() => handleMetaspaceClick('center_global_metaspace')}
      />
      <Button
        icon={isMarketplaceActive ? 'Marketplace-icon-filled' : 'Marketplace-icon'}
        className="global-marketplace-button"
        onClick={() => navigate('/marketplace')}
        tooltip={t('metaspace.marketplace')}
        aria-label={t('metaspace.marketplace')}
      />
      <Button
        icon="pi pi-palette"
        className={isStudioActive ? 'global-studio-button global-studio-button--active' : 'global-studio-button'}
        onClick={() => navigate('/studio', { state: { entry: 'nav' } })}
        tooltip={t('metaspace.studio')}
        aria-label={t('metaspace.studio')}
      />
      <Button
        icon="pi pi-check-square"
        className={isTaskListActive ? 'global-task-list-button global-task-list-button--active' : 'global-task-list-button'}
        onClick={() => handleMetaspaceClick('center_task_list')}
        tooltip={t('metaspace.taskList')}
        aria-label={t('metaspace.taskList')}
      />
      <NotificationBell className="desktop-notification-bell" onViewAllClick={handleViewAllNotifications} />
    </GlobalMetaspaceContainer>
  );
};

export default GlobalMetaspaces;
